import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useSelector, useDispatch } from 'react-redux';
import Helmet from 'react-helmet';

import Board from './components/Board';
import GroupFilterDropdown from './components/GroupFilterDropdown';
import LeaderboardTemplateCardShimmer from './components/LeaderboardTemplateCardShimmer';
import LeaderboardTemplatesGrid from './components/LeaderboardTemplatesGrid';
import NoGroupsMessage from './components/NoGroupsMessage';
import LeaderboardModal from './modals/LeaderboardModal';

import Button from '../../shared/_TBComponents/Button';
import HeaderText from '../../shared/_TBComponents/HeaderText';
import Layout from '../../shared/components/Layout/Layout';
import useWindowSize from '../../shared/hooks/useWindowSize';

import {
  setModalOpen, fetchTemplates, fetchGroups, clearAllBoards, setActiveFilters,
} from './ducks/leaderboardActions';

import iconSet from '../../shared/images/teambuildr-selection.json';
import mockLeaderboardTemplates from './mockLeaderboardTemplates';
import { BoxShadow } from '../../shared/GlobalStyles';

const PageWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding-top: 64px;
  position: relative;
`;

const HeaderRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 30px;
  margin-bottom: 15px;
  padding: 0 30px;
`;

const HeaderLeft = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const BackArrow = styled('div')`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #444;
  flex-shrink: 0;

  &:hover {
    color: #000;
  }
`;

const HeaderRight = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const SearchBar = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  background: white;
  border-radius: 8px;
  padding: 0 14px;
  box-shadow: ${BoxShadow};
  flex: 1 1 auto;
  min-width: 0;
`;

const SearchIconWrapper = styled('span')`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const SearchInput = styled('input')`
  border: none;
  outline: none;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #444;
  background: transparent;
  width: 300px;
  min-width: 0;

  &::placeholder {
    color: #bbb;
  }
`;

const ShimmerGrid = styled('div')`
  display: flex;
  flex-wrap: wrap;
  padding: 10px 20px;
`;

const BoardWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  flex-grow: 1;
  min-height: 0;
  flex-basis: 100%; /* Fix for Safari flex box wrong height issue */
  padding: 0 30px 15px;
`;

const Leaderboard = () => {
  const leaderboardsReference = useSelector(
    (state) => state.leaderboard.data.leaderboardsReference,
  );
  const view = useSelector((state) => state.leaderboard.ui.view);
  const activeFilters = useSelector((state) => state.leaderboard.ui.activeFilters);
  const groups = useSelector((state) => state.leaderboard.data.groups);
  const isLoadingGroups = useSelector((state) => state.leaderboard.ui.isLoadingGroups);
  const isLoadingTemplates = useSelector((state) => state.leaderboard.ui.isLoadingTemplates);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const dispatch = useDispatch();
  const windowSize = useWindowSize();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [groupsFetchStarted, setGroupsFetchStarted] = useState(false);

  const boards = Object.keys(leaderboardsReference).map((k) => leaderboardsReference[k]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (currentUser?.accountCode) {
      setGroupsFetchStarted(true);
      dispatch(fetchGroups(currentUser.accountCode));
    }
  }, [currentUser?.accountCode]);

  useEffect(() => {
    if (currentUser?.accountCode && !!groups.length) dispatch(fetchTemplates());
  }, [currentUser?.accountCode, groups.length]);

  useEffect(() => {
    if (view === 'home' && groups.length > 0) dispatch(fetchTemplates());
  }, [view]);

  const handleSetModalOpen = (modalOpen, modal, boardId) => {
    dispatch(setModalOpen(modalOpen, modal, boardId));
  };

  const addBoard = () => {
    handleSetModalOpen(true, 'Add Leaderboard');
  };

  const renderHomeContent = () => {
    if (!groupsFetchStarted || isLoadingGroups || isLoadingTemplates) {
      return (
        <ShimmerGrid>
          {mockLeaderboardTemplates.map((t) => (
            <LeaderboardTemplateCardShimmer key={t.id} />
          ))}
        </ShimmerGrid>
      );
    }
    if (groups.length === 0) return <NoGroupsMessage />;
    return <LeaderboardTemplatesGrid searchTerm={debouncedSearchTerm} />;
  };

  return (
    <Layout fullWidth>
      <PageWrapper>
        <Helmet>
          <title>Leaderboard | TeamBuildr</title>
        </Helmet>

        <HeaderRow>
          <HeaderLeft>
            {view === 'leaderboard' && (
              <BackArrow onClick={() => dispatch(clearAllBoards())}>
                <IcomoonReact iconSet={iconSet} size={16} icon='left-arrow' color='currentColor' />
              </BackArrow>
            )}
            <HeaderText fontWeight='300' letterSpacing='normal'>
              Leaderboard
            </HeaderText>
          </HeaderLeft>

          {view === 'home' && groupsFetchStarted && !isLoadingGroups && (
            <HeaderRight>
              <SearchBar>
                <SearchIconWrapper>
                  <IcomoonReact iconSet={iconSet} size={16} icon='magnifying-glass-alt' color='black' />
                </SearchIconWrapper>
                <SearchInput
                  placeholder='Search by exercise...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchBar>
              <GroupFilterDropdown
                groups={groups}
                height={40}
                selectedGroups={activeFilters.groups}
                onChange={(newGroups) => dispatch(setActiveFilters({
                  ...activeFilters,
                  groups: newGroups,
                }))}
              />
            </HeaderRight>
          )}
          {view === 'leaderboard' && (boards.length !== 3 && windowSize.width >= 768) && (
            <Button
              name='+ Add Board'
              bgColor='#444444'
              txtColor='white'
              noBorder
              customBorderRadius='8px'
              styledTextProps={{ fontWeight: 700, fontSize: 14 }}
              styledWrapperProps={{ height: '40px', padding: '0 16px' }}
              onClick={addBoard}
            />
          )}
        </HeaderRow>

        {view === 'home' && renderHomeContent()}
        {view === 'leaderboard' && (
          <BoardWrapper>
            {boards.map((board) => (
              <Board
                activeBoards={boards.length}
                id={board.id}
                key={board.id}
              />
            ))}
          </BoardWrapper>
        )}

        <LeaderboardModal />
      </PageWrapper>
    </Layout>
  );
};

export default Leaderboard;
